import React, { useState, useCallback } from "react";
import "./Auth.css";
import AutoPromoteLogo from './components/AutoPromoteLogo';
import { PUBLIC_SITE_URL } from "./config";
import EmailVerificationScreen from "./components/EmailVerificationScreen";

const loginHighlights = [
  {
    index: "01",
    label: "Create",
    copy: "Return to Studio, Cam Combiner, and your saved source media.",
  },
  {
    index: "02",
    label: "Promote",
    copy: "Continue clip discovery, AI Director, and destination-specific publishing.",
  },
  {
    index: "03",
    label: "Measure",
    copy: "Review your queue, connected channels, and available performance data.",
  },
];

const LoginForm = ({ onLogin, onClose, onResendVerification }) => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;
      setFormData(prev => ({ ...prev, [name]: value }));
      if (error) setError("");
      setNeedsVerification(false);
    },
    [error]
  );

  const handleSubmit = async event => {
    event.preventDefault();
    setError("");

    if (!agreed) {
      setError("Please agree to the Terms of Service and Privacy Policy before continuing.");
      return;
    }

    setIsLoading(true);

    try {
      const { email, password } = formData;
      try {
        localStorage.setItem("tosAgreed", "true");
      } catch (_) {
        // ignore storage failures (private mode, quota, etc.)
      }
      await onLogin(email, password);
    } catch (submitError) {
      console.error("Login error:", submitError);
      console.error("Error details:", {
        code: submitError.code,
        message: submitError.message,
      });

      const emailNotVerified = submitError.code === "auth/email-not-verified";
      setNeedsVerification(emailNotVerified);
      let message = emailNotVerified ? "Verify your email before signing in. " : "Login failed. ";
      if (submitError.code) {
        switch (submitError.code) {
          case "auth/invalid-credential":
            message += "Invalid email or password.";
            break;
          case "auth/user-not-found":
            message += "No account exists with this email.";
            break;
          case "auth/wrong-password":
            message += "Incorrect password.";
            break;
          case "auth/invalid-api-key":
            message += "Invalid Firebase configuration. Please check the setup.";
            break;
          case "auth/network-request-failed":
            message += "Network error. Please check your connection.";
            break;
          case "auth/too-many-requests":
            message += "Too many failed login attempts. Please try again later.";
            break;
          case "auth/email-not-verified":
            message += "Check Inbox, Spam, or Promotions, or resend the email below.";
            break;
          default:
            message += submitError.message;
        }
      } else {
        message += submitError.message || "Unknown error occurred";
      }

      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (needsVerification) {
    return <EmailVerificationScreen email={formData.email} onResend={onResendVerification} />;
  }

  return (
    <div className="auth-container auth-container--login">
      <div className="auth-stage">
        <section className="auth-stage__panel auth-stage__panel--brand">
          <div className="auth-brand-lockup">
            <AutoPromoteLogo />
          </div>
          <div className="auth-stage__badge">
            <i aria-hidden="true" />
            Secure workspace access
          </div>
          <h1 className="auth-stage__title">
            Pick up the workflow exactly where you left it.
          </h1>
          <p className="auth-stage__copy">
            Your creation tools, publishing queue, connected platforms, and team workspace are one
            sign-in away.
          </p>
          <div className="auth-stage__highlights">
            {loginHighlights.map(item => (
              <div key={item.index} className="auth-stage__highlight">
                <span className="auth-stage__highlight-mark">{item.index}</span>
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.copy}</small>
                </span>
              </div>
            ))}
          </div>
          <div className="auth-workspace-preview" aria-hidden="true">
            <div className="auth-workspace-preview__topline">
              <span>Workspace overview</span>
              <small>Ready</small>
            </div>
            <div className="auth-workspace-preview__grid">
              <span>
                <small>Create</small>
                <strong>Your media</strong>
              </span>
              <span>
                <small>Publish</small>
                <strong>Queue ready</strong>
              </span>
              <span>
                <small>Edit</small>
                <strong>Studio ready</strong>
              </span>
            </div>
          </div>
        </section>

        <form onSubmit={handleSubmit} className="auth-form auth-form--modal">
          <div className="auth-form__header">
            <div className="auth-form__topline">
              <div className="auth-form__eyebrow">Sign In</div>
              <span className="auth-form__secure">
                <i aria-hidden="true" />
                Protected access
              </span>
            </div>
            <h2 className="auth-title auth-title--left">Welcome back</h2>
            <p className="auth-subtitle">Enter your details to open the creator workspace.</p>
          </div>
          {error && <div className="error-message">{error}</div>}


          <div className="auth-form__fields">
            <div className="form-group">
              <label className="form-label" htmlFor="login-email">
                Email
              </label>
              <div className="auth-input-shell">
                <span aria-hidden="true">@</span>
                <input
                  id="login-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter your email"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="login-password">
                Password
              </label>
              <div className="auth-input-shell">
                <span aria-hidden="true">◆</span>
                <input
                  id="login-password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>
          </div>

          <div className="form-group auth-form__inline">
            <div className="terms-row terms-row--compact">
              <input
                id="agreeTerms"
                type="checkbox"
                checked={agreed}
                onChange={event => setAgreed(event.target.checked)}
              />
              <label htmlFor="agreeTerms" className="form-label">
                I agree to the{" "}
                <a href={`${PUBLIC_SITE_URL}/terms`} target="_blank" rel="noreferrer">
                  Terms
                </a>{" "}
                and{" "}
                <a href={`${PUBLIC_SITE_URL}/privacy`} target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                .
              </label>
            </div>
            <button
              type="button"
              onClick={() => {
                if (onClose) onClose();
                const email = encodeURIComponent(formData.email || "");
                window.location.hash = email
                  ? `#/forgot-password?email=${email}`
                  : "#/forgot-password";
              }}
              className="link-like"
            >
              Forgot password?
            </button>
          </div>

          <button type="submit" disabled={isLoading || !agreed} className="auth-button">
            {isLoading ? (
              <>
                <span className="loading-spinner" />
                Signing in...
              </>
            ) : (
              "Enter AutoPromote"
            )}
          </button>

          <div className="auth-form__note">
            <span aria-hidden="true">✓</span>
            <span>Firebase authentication protects access to your AutoPromote workspace.</span>
          </div>

          <div className="auth-action-row">
            <button
              type="button"
              className="auth-home-button"
              onClick={() => {
                window.location.href = PUBLIC_SITE_URL || "/";
              }}
            >
              Back Home
            </button>

            <button
              type="button"
              onClick={() => {
                if (onClose) onClose();
              }}
              className="auth-link auth-link--inline"
            >
              New to AutoPromote? Create an account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
